import { DatasourceSingleton } from '@/common/di';
import { showBothHazardView } from '@/common/helpers/sizes';
import {
  CentreEntity,
  ClosureEntity,
  DropdownGroup,
  EntitySubType,
  HazardEntity,
  HazardsEntity,
  HazardsRepositoryImpl,
  HazardType,
  hazardTypeRoute,
  HazardView,
  IncidentEntity,
  MapMode,
  SelectedHazard,
  SelectedHazardViewType,
  useMapStore,
  WarningEntity,
  WarningSubTypes,
} from '@/features/hazards';
import { is511Hazard } from '@/features/hazards/presentation/helpers';

import {
  AirQualitySubType,
  BlizzardSubType,
  BushfireSubType,
  CentreSubType,
  ClosureSubType,
  DamagingWindsSubType,
  ExtremeColdSubType,
  FlashFloodSubType,
  FloodSubType,
  FreezingRainSubType,
  HazardSubType,
  HeatwaveSubType,
  HumanPandemicSubType,
  IceJamSubType,
  LakeFloodingSubType,
  OtherWarningSubType,
  PowerOutageEmoSubType,
  RiverFloodingSubType,
  SeasonalOverlandFloodingSubType,
  SevereWinterWeatherSubType,
  SmokeSubType,
  StormSubType,
  StormSurgeSubType,
  StructureFireSubType,
  TornadoSubType,
} from '@/features';
import { i18n } from '@/locales';
import { pageTitleHandler, RouteNames } from '@/router';
import { useOnline } from '@vueuse/core';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { container } from 'tsyringe';
import { computed, ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { sortStringifiedDateTimes } from '../helpers';
import { AnnouncementKey } from '../helpers/announcement-highlight';

const { t, locale } = i18n.global;

export const useHazardsStore = defineStore('hazardsStore', () => {
  // This is to check if the user is online and gracefully handle the offline state
  // @see - https://vueuse.org/core/useOnline/
  const isOnline = useOnline();

  // TODO: datasource and repository should be injected as arguments
  const datasource = container.resolve(DatasourceSingleton).hazards;
  const s3Datasource = container.resolve(DatasourceSingleton).s3Hazards;

  const router = useRouter();

  const mapStore = useMapStore();

  const hazardRepository = new HazardsRepositoryImpl(datasource, s3Datasource);

  const searchTerm = ref<string>('');
  const mapMode = ref<MapMode>(MapMode.Default);
  const selectedDateIndex = ref<number>(0);
  const selectedHazard = ref<SelectedHazard | null>(null);

  const dates = computed(() => {
    // Track locale changes
    void locale.value;

    const datesArray = ['Today', 'Tomorrow'];

    const today = DateTime.now();

    const in2Days = today.plus({ days: 2 });
    const in3Days = today.plus({ days: 3 });

    datesArray.push(in2Days.toFormat('dd MMM'));
    datesArray.push(in3Days.toFormat('dd MMM'));

    return datesArray;
  });

  const typeSections = computed<DropdownGroup[]>(() => [
    {
      label: t('i3H4C4ZdrYAfFoCtkaeiC'),
      items: [
        { label: t('xK9mPqAirQTy1EmgFilter'), hazardType: HazardType.Warning, hazardSubType: Object.values(AirQualitySubType) },
        { label: t('pN8vBliz4dQSubTyFlt'), hazardType: HazardType.Warning, hazardSubType: Object.values(BlizzardSubType) },
        { label: t('eC4vXTmn9yKpR7jLs2Fw'), hazardType: HazardType.Warning, hazardSubType: Object.values(ExtremeColdSubType) },
        { label: t('fF8uQrWh3xNvM6cPb1Zk'), hazardType: HazardType.Warning, hazardSubType: Object.values(FlashFloodSubType) },
        { label: t('mzQr-TDOGQZBiS0JkvXI0'), hazardType: HazardType.Warning, hazardSubType: Object.values(FloodSubType) },
        { label: t('fR2wKdBn5tGmL8pH0yQv'), hazardType: HazardType.Warning, hazardSubType: Object.values(FreezingRainSubType) },
        {
          label: t('ctjac0YhKWnglRa8y0nH3'),
          hazardType: HazardType.Warning,
          hazardSubType: Object.values(OtherWarningSubType),
        },
        { label: t('fB1MbPSnjVc_XvNwBuJOT'), hazardType: HazardType.Warning, hazardSubType: Object.values(HazardSubType) },
        { label: t('fpJcEG_IEYPlCSf1W-a0Q'), hazardType: HazardType.Warning, hazardSubType: Object.values(HeatwaveSubType) },
        { label: t('ry9wjOHWz61gShaxRgrU4'), hazardType: HazardType.Warning, hazardSubType: Object.values(HumanPandemicSubType) },
        { label: t('iJ6pNtVe7xCmB4sL1wRk'), hazardType: HazardType.Warning, hazardSubType: Object.values(IceJamSubType) },
        { label: t('lF9mSqTr4vXcB8zW5eKn'), hazardType: HazardType.Warning, hazardSubType: Object.values(LakeFloodingSubType) },
        { label: t('pO2nKtXm8vBL4cQ7wFze'), hazardType: HazardType.Warning, hazardSubType: Object.values(PowerOutageEmoSubType) },
        { label: t('rF7nKmPt5vXcQ2wB8sLj'), hazardType: HazardType.Warning, hazardSubType: Object.values(RiverFloodingSubType) },
        { label: t('sO4tXmBn9rKpV7cL2wQf'), hazardType: HazardType.Warning, hazardSubType: Object.values(SeasonalOverlandFloodingSubType) },
        { label: t('TDAz1QS4kd1D0pn2-NQTn'), hazardType: HazardType.Warning, hazardSubType: Object.values(StormSubType) },
        { label: t('sW1mBnKt6pRvX3cQ8yLf'), hazardType: HazardType.Warning, hazardSubType: Object.values(SevereWinterWeatherSubType) },
        { label: t('3yqqu_rawaI6Ti2R4jBUS'), hazardType: HazardType.Warning, hazardSubType: Object.values(SmokeSubType) },
        { label: t('sS6xKmNr4pBL7cQ9wFtv'), hazardType: HazardType.Warning, hazardSubType: Object.values(StormSurgeSubType) },
        { label: t('VXrGaiFmQF-aaah1McCVE'), hazardType: HazardType.Warning, hazardSubType: Object.values(StructureFireSubType) },
        { label: t('tN3vKmXr8pBL5cQ2wFje'), hazardType: HazardType.Warning, hazardSubType: Object.values(TornadoSubType) },
        { label: t('5hRAby0mkv1XUKMYWzBRM'), hazardType: HazardType.Warning, hazardSubType: Object.values(BushfireSubType) },
        { label: t('KT_4BlHJ7ZrJifsjj7JeT'), hazardType: HazardType.Warning, hazardSubType: Object.values(DamagingWindsSubType) },
      ],
    },
    {
      label: t('IdaFPyJzD-KUpjlTXGsao'),
      items: [
        { label: t('4pxFgiGCG3yT9K1woJ_Ll'), hazardType: HazardType.Centre, hazardSubType: [CentreSubType.EvacuationCentreEntity] },
        { label: t('gC3nTrKm8xPL5cQ2vFwe'), hazardType: HazardType.Centre, hazardSubType: [CentreSubType.GenericCentreEntity] },
        { label: t('zZ2Tg7mII5zI4-lYyceQg'), hazardType: HazardType.Centre, hazardSubType: [CentreSubType.EvacuationRespiteCentreEntity] },
      ],
    },
    {
      label: t('gDfl0cOHihK9_UA8AcJJT'),
      items: [
        { label: t('6-HnmLiXOWKyedDiYI6p8'), hazardType: HazardType.Closure, hazardSubType: [ClosureSubType.FacilityOrParkClosureEntity] },
        { label: t('pC5mNtKr2xPL8cQ9vFwe'), hazardType: HazardType.Closure, hazardSubType: [ClosureSubType.ParkClosureEntity] },
        { label: t('zOSJy1oRtXyCmEyulLrUu'), hazardType: HazardType.Closure, hazardSubType: [ClosureSubType.SchoolClosureEntity] },
        { label: t('genericClosureLabel'), hazardType: HazardType.Closure, hazardSubType: [ClosureSubType.GenericClosureEntity] },
      ],
    },
    { label: t('tE7WsidrEZnRAqb0Iw4bc'), items: [], hazardType: HazardType.Incident },
    { label: t('qdbLtnywXWkG2to5yLPI8'), items: [], hazardType: HazardType.Incident },
    {
      label: t('tR5mNtKr8xPL2cQ6vFwe'),
      items: [
        { label: t('aC3mNtKr7xPL4cQ5vFwe') },
        { label: t('bC8mNtKr2xPL6cQ3vFwe') },
        { label: t('hR5mNtKr4xPL7cQ2vFwe') },
        { label: t('mV9mNtKr6xPL3cQ8vFwe') },
        { label: t('nA4mNtKr8xPL5cQ7vFwe') },
        { label: t('qL9JfyMYjR3sSxVSVhO5l') },
        { label: t('rI4mNtKr9xPL3cQ7vFwe') },
        { label: t('rW6mNtKr2xPL8cQ4vFwe'), defaultEnabled: false },
        { label: t('tA2mNtKr3xPL9cQ6vFwe') },
      ],
    },
  ]);

  const buildTypeFilters = () => {
    const filters: Record<string, Record<'Default' | string, boolean>> = {};

    for (const section of typeSections.value) {
      if (!section.items.length) {
        filters[section.label] = { Default: true };
      } else {
        filters[section.label] = Object.fromEntries(section.items.map((item) => [item.label, item.defaultEnabled ?? true]));
      }
    }

    return filters;
  };

  const typesFilters = ref(buildTypeFilters());

  watchEffect(() => {
    // Reset the typesFilters when the typeSections change (i.e. locale change)
    typesFilters.value = buildTypeFilters();
  });

  const isSomeTypeFilterActive = computed<boolean>(() =>
    Object.values(typesFilters.value).some((section) => Object.values(section).some((checked) => checked))
  );

  const hazardView = ref<HazardView>(!showBothHazardView.value ? HazardView.List : HazardView.Both);

  const activeView = computed<HazardView>(() =>
    !showBothHazardView.value && hazardView.value === HazardView.Both ? HazardView.Map : hazardView.value
  );

  const setActiveView = (view: HazardView) => {
    if (view === hazardView.value) return;

    let sanitisedView = view;

    if (!showBothHazardView.value && sanitisedView === HazardView.Both) {
      sanitisedView = HazardView.Map;
    }

    router.push({ query: { ...(router.currentRoute.value.query ?? {}), view: sanitisedView.toLowerCase() } });

    hazardView.value = sanitisedView;
  };

  const setMapMode = (mode: MapMode) => {
    if (mode === mapMode.value) return;

    const query = { view: hazardView.value.toLowerCase() };

    router.push({ name: RouteNames.Hazards, query });

    mapMode.value = mode;

    // Reset the selected date index to prevent the date from being out of bounds
    selectedDateIndex.value = 0;
  };

  const setSelectedDateIndex = (index: number) => {
    if (index === selectedDateIndex.value || index < 0 || index > 3) return;

    const sanitisedIndex = index;

    router.push({ query: { ...(router.currentRoute.value.query ?? {}), date: sanitisedIndex.toString() } });

    selectedDateIndex.value = sanitisedIndex;
  };

  const setSelectedHazard = (hazard: SelectedHazard | null) => {
    let eventId = hazard?.eventId;

    if (hazard?.hazardType) {
      const hazardEntity = (hazards.value[hazard?.hazardType] as HazardEntity[])?.find(
        (h: HazardEntity) => h.id === hazard?.hazardId
      ) as HazardEntity;

      if (hazardEntity) {
        const { title } = hazardEntity;

        if (!eventId?.length) {
          eventId = hazardEntity?.eventId;
        }

        if (title?.length) {
          const pageTitle = `${title} - ${pageTitleHandler.value}`;

          pageTitleHandler.value = pageTitle;
        }
      }
    }

    selectedHazard.value = hazard ? { ...hazard, eventId: eventId ?? '' } : null;
  };

  /**
   * The selected announcement, or `null` when nothing is selected.
   *
   * Used to isolate the selection on the map: this hazard's map objects are drawn at full opacity,
   * every other hazard is dimmed — including hazards in the same event.
   */
  const selectedAnnouncement = computed<AnnouncementKey | null>(() =>
    selectedHazard.value ? { hazardId: selectedHazard.value.hazardId } : null
  );

  const retryButton = ref(false);

  const isLoadingHazards = ref(false);
  const isLoadingEnrichedWarning = ref(false);

  const hazards = ref<HazardsEntity>({
    incidents: [],
    warnings: [],
    centres: [],
    closures: [],
    events: [],
  });

  const filteredSectionHazards = computed(() => {
    const activeFilters: Set<(typeof EntitySubType)[keyof typeof EntitySubType]> = new Set();

    //! Warnings filters
    const warningSection = typeSections.value.find((typeSection) => typeSection.label === t('i3H4C4ZdrYAfFoCtkaeiC'));
    Object.entries(typesFilters.value[t('i3H4C4ZdrYAfFoCtkaeiC')])
      .filter((warningFilter) => warningFilter[1])
      .forEach(([filter]) =>
        warningSection?.items.find((item) => item.label === filter)?.hazardSubType?.forEach((warningType) => activeFilters.add(warningType))
      );

    // Filter out total fire bans (until the API is update to not include them in warnings endpoint)
    const warnings = hazards.value.warnings.filter((warning) => warning.entitySubType !== WarningSubTypes.TotalFireBanWarningEntity);
    const filteredWarnings = warnings.filter((warning) => activeFilters.has(warning.entitySubType!));

    //! Transportation
    const transportationFilters = typesFilters.value[t('tR5mNtKr8xPL2cQ6vFwe')] ?? {};

    const enabledTransportationTypes = new Set<string>();
    if (transportationFilters[t('aC3mNtKr7xPL4cQ5vFwe')]) enabledTransportationTypes.add('airspace-closure');
    if (transportationFilters[t('bC8mNtKr2xPL6cQ3vFwe')]) enabledTransportationTypes.add('bridge-closure');
    if (transportationFilters[t('hR5mNtKr4xPL7cQ2vFwe')]) enabledTransportationTypes.add('hazardous-road-conditions');
    if (transportationFilters[t('mV9mNtKr6xPL3cQ8vFwe')]) enabledTransportationTypes.add('motor-vehicle-accident');
    if (transportationFilters[t('nA4mNtKr8xPL5cQ7vFwe')]) enabledTransportationTypes.add('notice-to-airmen');
    if (transportationFilters[t('qL9JfyMYjR3sSxVSVhO5l')]) enabledTransportationTypes.add('road-closure');
    if (transportationFilters[t('rI4mNtKr9xPL3cQ7vFwe')]) enabledTransportationTypes.add('road-incident');
    if (transportationFilters[t('rW6mNtKr2xPL8cQ4vFwe')]) enabledTransportationTypes.add('road-work');
    if (transportationFilters[t('tA2mNtKr3xPL9cQ6vFwe')]) enabledTransportationTypes.add('train-accident');

    // Catch-all for transportation hazards whose transportationType is missing or doesn't match any sub-filter.
    // Show these only when every sub-filter is active, so they aren't silently hidden.
    const showOtherTransportation = enabledTransportationTypes.size === Object.keys(transportationFilters).length;

    const allTransportation: HazardEntity[] = [
      ...hazards.value.incidents.filter((i) => i.isTransportation),
      ...hazards.value.closures.filter((c) => c.isTransportation),
    ];

    const filteredTransportation = allTransportation
      .filter((h) => {
        const tt = (h as IncidentEntity | ClosureEntity).transportationType;
        if (!tt || !enabledTransportationTypes.has(tt)) return showOtherTransportation;
        return true;
      })
      .sort((a, b) => sortStringifiedDateTimes(a.publishedAt, b.publishedAt));

    //! Natural hazards
    const naturalHazardsChecked = typesFilters.value[t('qdbLtnywXWkG2to5yLPI8')]?.['Default'];
    const naturalHazards = hazards.value.incidents
      .filter((incident) => incident.isNaturalHazard && !incident.isTransportation)
      .sort((a, b) => sortStringifiedDateTimes(a.publishedAt, b.publishedAt));
    const filteredNaturalHazards = naturalHazardsChecked ? naturalHazards : [];

    //! Incidents filters
    const incidentChecked = typesFilters.value[t('tE7WsidrEZnRAqb0Iw4bc')]?.['Default'];

    // TODO: Localise Burn offs and Prescribed Burns once needed
    const nonSpecialIncidents = hazards.value.incidents.filter(
      (incident) => incident.title !== 'Burn Off' && !incident.isNaturalHazard && !incident.isTransportation
    );
    const filteredIncidents = incidentChecked ? nonSpecialIncidents : [];

    //! Closures
    const closuresSection = typeSections.value.find((typeSection) => typeSection.label === t('gDfl0cOHihK9_UA8AcJJT'));
    Object.entries(typesFilters.value[t('gDfl0cOHihK9_UA8AcJJT')])
      .filter((closureFilter) => closureFilter[1])
      .forEach(([filter]) =>
        closuresSection?.items
          .find((item) => item.label === filter)
          ?.hazardSubType?.forEach((closureType) => activeFilters.add(closureType))
      );
    const closures = hazards.value.closures.filter((c) => !c.isTransportation);
    const filteredClosures = closures
      .filter((closure) => activeFilters.has(closure.entitySubType!))
      .sort((a, b) => sortStringifiedDateTimes(a.publishedAt, b.publishedAt));

    //! Centres
    const centresSection = typeSections.value.find((typeSection) => typeSection.label === t('IdaFPyJzD-KUpjlTXGsao'));
    Object.entries(typesFilters.value[t('IdaFPyJzD-KUpjlTXGsao')])
      .filter((centreFilter) => centreFilter[1])
      .forEach(([filter]) =>
        centresSection?.items.find((item) => item.label === filter)?.hazardSubType?.forEach((centreType) => activeFilters.add(centreType))
      );

    const centres = hazards.value.centres;
    const filteredCentres = centres.filter((centre) => activeFilters.has(centre.entitySubType!));

    //! Fire Danger Ratings
    const filtered = {
      incidents: filteredIncidents,
      warnings: filteredWarnings,
      centres: filteredCentres,
      outagesAndClosures: filteredClosures,
      naturalHazards: filteredNaturalHazards,
      transportation: filteredTransportation,
    };

    return filtered;
  });

  const getHazardsByEvent = (eventId?: string) => {
    return {
      incidents: filteredSectionHazards.value.incidents.filter((incident) => incident.eventId === eventId) as IncidentEntity[],
      warnings: filteredSectionHazards.value.warnings.filter((warning) => warning.eventId === eventId) as WarningEntity[],
      outagesAndClosures: filteredSectionHazards.value.outagesAndClosures.filter(
        (outagesAndClosure) => outagesAndClosure.eventId === eventId
      ) as ClosureEntity[],
      centres: filteredSectionHazards.value.centres.filter((centre) => centre.eventId === eventId) as CentreEntity[],
      naturalHazards: filteredSectionHazards.value.naturalHazards.filter(
        (naturalHazard) => naturalHazard.eventId === eventId
      ) as IncidentEntity[],
      transportation: filteredSectionHazards.value.transportation.filter(
        (transportation) => transportation.eventId === eventId
      ) as HazardEntity[],
    };
  };

  const fetchAllHazards = async (hazardTypes: HazardType[], language?: string) => {
    if (isOnline.value) {
      isLoadingHazards.value = true;
      const fetchPromises = hazardTypes.map((hazardType) => fetchHazards(hazardType, language));
      await Promise.all(fetchPromises);
      isLoadingHazards.value = false;
    }
  };

  const fetchHazards = async (hazardType: HazardType, languageOverride?: string) => {
    const language = languageOverride || i18n.global.locale.value != 'en-CA' ? i18n.global.locale.value : undefined;
    const languageCode = language?.split('-')[0]; // TODO: This is actually sending the language code, not the localisation code, we should refactor this once the API supports `fr-CA` rather than `fr`.

    const maybeHazards = await hazardRepository.getHazards(hazardType, languageCode);
    pipe(
      maybeHazards,
      fold(
        () => {
          // toast.addToast({
          //   text: mapErrorToMessage(error),
          //   scheme: 'error',
          // });
        },
        (hazard) => {
          // Guaranteed to be a hazard of the correct type
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          hazards.value[hazardType] = hazard as any[];
        }
      )
    );
  };

  /**
   * Handles the selected hazard.
   * @param selectedHazard - The selected hazard
   * @returns
   */
  const handleSelectedHazard = (hazard: SelectedHazard) => {
    if (
      selectedHazard.value?.hazardId === hazard.hazardId &&
      selectedHazard.value?.viewType === hazard.viewType &&
      selectedHazard.value?.featureId === hazard.featureId
    ) {
      return;
    }

    let eventId = hazard?.eventId;

    if (!eventId && hazard?.hazardType) {
      eventId =
        ((hazards.value[hazard?.hazardType] as HazardEntity[])?.find((h: HazardEntity) => h.id === hazard?.hazardId) as HazardEntity)
          ?.eventId ?? '';
    }

    const relatedEvent = hazards.value.events.find((event) => event.id === eventId);
    const is511HazardEvent = is511Hazard(relatedEvent);
    const currentSelectedPopupHazard = mapStore.selectedPopupHazard;

    // Check if the selected popup hazard has changed
    // This ensure we don't re-set the selected popup hazard if the same hazard is selected again (e.g. when the hazards are re-fetched)
    const selectedPopupHazardChanged =
      currentSelectedPopupHazard?.hazardId !== hazard.hazardId || currentSelectedPopupHazard?.featureId !== hazard.featureId;

    if (
      activeView.value !== HazardView.List &&
      selectedPopupHazardChanged &&
      (hazard.viewType === SelectedHazardViewType.Popup || is511HazardEvent)
    ) {
      mapStore.selectedPopupHazard = { ...hazard, eventId };
    }

    if (hazard.viewType === SelectedHazardViewType.Modal) {
      mapStore.selectedPopupHazard = null;
    }

    setSelectedHazard({ ...hazard, eventId });
  };

  /**
   * Toggles the selected hazard by navigating to the hazard route.
   * If the hazard is not null, it will navigate to the hazard page.
   * If the hazard is null, it will navigate to the hazards page (and clear the selected hazard).
   *
   * @param hazard - The selected hazard or null
   */
  const toggleSelectHazard = (hazard: SelectedHazard | null) => {
    if (hazard) {
      router.push({
        name: RouteNames.HazardById,
        params: {
          hazardType: hazardTypeRoute(hazard.hazardType),
          hazardId: hazard.hazardId,
        },
        query: {
          display: hazard.viewType,
          ...((hazard.featureId && { featureId: hazard.featureId }) || {}),
        },
      });
    } else {
      if (selectedHazard.value) {
        setSelectedHazard(null);
      }

      router.push({
        name: RouteNames.Hazards,
      });
    }
  };

  // Sources
  // KUDO1-6688, updates the hazard polygons source to include warnings and storm natural hazards polygons.
  const getHazardPolygonsGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const sectionKeys: (keyof typeof filteredSectionHazards.value)[] = [
      'warnings',
      'outagesAndClosures',
      'centres',
      'naturalHazards',
      'transportation',
    ];

    const features = sectionKeys
      .flatMap((key) => filteredSectionHazards.value[key].flatMap((hazard) => hazard.polygonFeatures))
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    return {
      type: 'FeatureCollection',
      features,
    };
  });

  const getHazardFireShapePolygonsGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const incidentsPolygonFeatures = filteredSectionHazards.value.incidents
      .flatMap((incident) => incident.polygonFeatures)
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    return {
      type: 'FeatureCollection',
      features: incidentsPolygonFeatures,
    };
  });

  const getHazardPolylinesGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const sectionKeys: (keyof typeof filteredSectionHazards.value)[] = [
      'warnings',
      'incidents',
      'outagesAndClosures',
      'centres',
      'naturalHazards',
      'transportation',
    ];

    const features = sectionKeys
      .flatMap((key) => filteredSectionHazards.value[key].flatMap((hazard) => hazard.polylineFeatures))
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    return {
      type: 'FeatureCollection',
      features,
    };
  });

  const getHazardPointGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const sectionKeys: (keyof typeof filteredSectionHazards.value)[] = [
      'warnings',
      'incidents',
      'outagesAndClosures',
      'centres',
      'naturalHazards',
      'transportation',
    ];

    const features = sectionKeys
      .flatMap((key) => filteredSectionHazards.value[key].flatMap((hazard) => hazard.pointFeatures))
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    return {
      type: 'FeatureCollection',
      features,
    };
  });

  return {
    hazards,
    mapMode,
    setMapMode,
    searchTerm,
    activeView,
    typesFilters,
    isSomeTypeFilterActive,
    retryButton,
    selectedHazard,
    selectedAnnouncement,
    isLoadingHazards,
    isLoadingEnrichedWarning,
    fetchHazards,
    setActiveView,
    getHazardsByEvent,
    fetchAllHazards,
    handleSelectedHazard,
    getHazardPointGeoSource,
    getHazardPolygonsGeoSource,
    getHazardFireShapePolygonsGeoSource,
    getHazardPolylinesGeoSource,
    filteredSectionHazards,
    selectedDateIndex,
    setSelectedDateIndex,
    dates,
    toggleSelectHazard,
    typeSections,
  };
});
